import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { first, Subject } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { Pair } from "../../obj/Pair";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { AccountConsumption } from "../../obj/AccountConsumption";
import { Account } from "../../entity/Account";
import { ConsumptionDataType } from "../../obj/ConsumptionDataType";
import { FormsModule } from "@angular/forms";
import { ErrorImmutable } from "../../obj/ErrorImmutable";

@Component({
    selector: "kilowant-account-consumption",
    templateUrl: "../../../html/incs/account-consumption.html",
    imports: [FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountConsumptionComponent extends AbstractBuyerComponent {
    @Output() finished = new EventEmitter<boolean>();
    @Output() cancelled = new EventEmitter<boolean>();

    consumptionYears: Pair[] = [];
    currentConsumption: string[] = ['', '', '', '', '', '', '', '', '', '', '', ''];
    consumptionFullness: boolean[][] = [];
    yearEnd: number = 0;
    selectedConsumptionYear: number = 0;
    loaded = false;

    months: string[] = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    consumptions: AccountConsumption[][] = [];
    @Output("consumptionsChange") consumptionsEmitter: EventEmitter<AccountConsumption[][]> = new EventEmitter<AccountConsumption[][]>();
    @Input("consumptions") set setConsumptionsValue(value: AccountConsumption[][]) {
        this.consumptions = value;
        this.cdr.markForCheck();
    }
    account: Account;
    @Output("accountChange") accountEmitter: EventEmitter<Account> = new EventEmitter<Account>();
    @Input("account") set setAccountValue(value: Account) {
        this.account = value;
        this.cdr.markForCheck();
    }
    @Input() newAccount: boolean = false;

    rewriteEstimationMode: boolean = false;

    @Output("changed") changedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output("setEstimated") setEstimatedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() callUpdate: Subject<boolean>;
    @Input() callCancel: Subject<boolean>;
    @Input() activated: boolean = false;
    @Input() isAIBased: boolean = false;

    started: boolean = false;
    emptyConsumptionMode: boolean = true;
    showErrorLaunched: boolean = false;
    consumptionDataType: ConsumptionDataType = ConsumptionDataType.FULL;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.eventSubscriptions.push(
            this.callUpdate.subscribe((v: any) => {
                this.updateAccountConsumption();
                this.cdr.markForCheck();
            })
        );
        this.eventSubscriptions.push(
            this.callCancel.subscribe((v: any) => {
                this.cancel();
                this.cdr.markForCheck();
            })
        );
    }

    ngOnChanges(): void {
        if (this.activated && !this.started) {
            this.startComponent();
            this.started = true;
            this.cdr.markForCheck();
        }
    }

    startComponent() {
        if (this.account == null || this.account.utility == null) {
            this.cancel();
            return;
        }
        if (this.newAccount) {
            this.consumptionYears = [];
            this.refreshTime().then(x => {
                let startYear = this.currentDate.year - 3;
                this.yearEnd = this.currentDate.year;
                for (let i = startYear; i <= this.yearEnd; i++) {
                    this.consumptionYears.push(new Pair(i, false));
                    if (this.consumptions.length < (this.yearEnd - startYear + 1)) {
                        this.consumptions.push([]);
                    }
                }
                this.selectConsumptionYear(this.consumptionYears[this.consumptionYears.length - 2].key);
                this.loaded = true;
                this.cdr.markForCheck();
            });
        } else {
            this.generalService.getAccountConsumptionType(this.account).pipe(first()).subscribe(
                (res: ConsumptionDataType) => {
                    if (res == ConsumptionDataType.ESTIMATED) {
                        this.rewriteEstimationMode = true;
                        this.setEstimatedEmitter.emit(true);
                        this.fillEstimatedConsumption();
                        this.loaded = true;
                        this.cdr.markForCheck();
                    } else {
                        this.generalService.getAccountConsumptionYears(this.account).then((years) => {
                            this.consumptionYears = years;
                            this.yearEnd = this.consumptionYears[this.consumptionYears.length - 1].key;
                            for (let i = 0; i < this.consumptionYears.length; i++) {
                                this.consumptions.push([]);
                                if (this.consumptionYears[i].value && !this.rewriteEstimationMode) {
                                    this.emptyConsumptionMode = false;
                                    this.showErrorLaunched = true;
                                }
                            }
                            this.selectConsumptionYear(this.consumptionYears[this.consumptionYears.length - 2].key);
                            this.loaded = true;
                            this.cdr.markForCheck();
                        });
                    }
                });
        }
        this.refreshFullness();
        this.cdr.markForCheck();
    }

    fillEstimatedConsumption(): void {
        this.generalService.getAccountConsumptionYears(this.account).then((years) => {
            this.consumptionYears = [];
            this.yearEnd = years[years.length - 1].key;
            this.consumptionYears.push(years[years.length - 1]);
            this.generalService.getAccountConsumptions(this.account, years[years.length - 1].key).then((consumptions: any) => {
                this.consumptions[0] = [];
                this.consumptionFullness[0] = [];
                for (let i = 0; i < consumptions.length; i++) {
                    this.consumptions[0].push(consumptions[i].key);
                    this.consumptionFullness[0].push(consumptions[i].value);
                }
                if (years.length > 1) {
                    this.generalService.getAccountConsumptions(this.account, years[years.length - 2].key).then((consumptions2: any) => {
                        for (let i = 0; i < consumptions2.length; i++) {
                            if (this.consumptions[0][i].consumption == null && consumptions2[i].key.consumption != null) {
                                this.consumptions[0][i] = consumptions2[i].key;
                                this.consumptionFullness[0][i] = consumptions[i].value;
                            }
                        }
                        this.currentConsumption = ['', '', '', '', '', '', '', '', '', '', '', ''];
                        for (let i = 0; i < 12; i++) {
                            this.currentConsumption[i] = "";
                            if (this.consumptions[0][i].consumption != null) {
                                this.currentConsumption[i] = this.consumptions[0][i].consumption!.toString();
                            }
                        }
                        this.cdr.markForCheck();
                    });
                } else {
                    this.currentConsumption = ['', '', '', '', '', '', '', '', '', '', '', ''];
                    for (let i = 0; i < 12; i++) {
                        this.currentConsumption[i] = "";
                        if (this.consumptions[0][i].consumption != null) {
                            this.currentConsumption[i] = this.consumptions[0][i].consumption!.toString();
                        }
                    }
                    this.cdr.markForCheck();
                }
            });
        });
    }

    selectConsumptionYear(year: number) {
        this.selectedConsumptionYear = year;
        if (this.consumptions[this.yearEnd - this.selectedConsumptionYear].length == 0) {
            if (this.newAccount) {
                this.consumptions[this.yearEnd - this.selectedConsumptionYear] = [];
                this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear] = [];
                for (let m = 1; m <= 12; m++) {
                    let consumption = new AccountConsumption();
                    consumption.consumptionYear = this.selectedConsumptionYear;
                    consumption.consumptionMonth = m;
                    consumption.consumption = null;
                    this.consumptions[this.yearEnd - this.selectedConsumptionYear].push(consumption);
                    this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear].push(false);
                }
                for (let i = 0; i < 12; i++) {
                    this.currentConsumption[i] = "";
                    if (this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption != null) {
                        this.currentConsumption[i] = this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption!.toString();
                        this.emptyConsumptionMode = false;
                        this.showErrorLaunched = true;
                    }
                }
                this.cdr.markForCheck();
            } else {
                this.generalService.getAccountConsumptions(this.account, this.selectedConsumptionYear).then((consumptions) => {
                    this.consumptions[this.yearEnd - this.selectedConsumptionYear] = [];
                    this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear] = [];
                    for (let i = 0; i < consumptions.length; i++) {
                        this.consumptions[this.yearEnd - this.selectedConsumptionYear].push(consumptions[i].key);
                        this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear].push(consumptions[i].value);
                    }
                    this.currentConsumption = ['', '', '', '', '', '', '', '', '', '', '', ''];
                    for (let i = 0; i < 12; i++) {
                        this.currentConsumption[i] = "";
                        if (this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption != null) {
                            this.currentConsumption[i] = this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption!.toString();
                            this.emptyConsumptionMode = false;
                            this.showErrorLaunched = true;
                        }
                    }
                    this.cdr.markForCheck();
                });
            }
        } else {
            this.currentConsumption = ['', '', '', '', '', '', '', '', '', '', '', ''];
            for (let i = 0; i < 12; i++) {
                this.currentConsumption[i] = (this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption == null) ? "" : this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption!.toString();
            }
            this.cdr.markForCheck();
        }
    }

    showFullnessClass(p: number): string {
        if (this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear] != null) {
            if (!this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear][p]) {
                return " non-full-consumption";
            }
        }
        return "";
    }

    changeConsumption(i: number, consumption: string) {
        if (consumption == "") {
            this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption = null;
            this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear][i] = false;
        } else {
            this.consumptions[this.yearEnd - this.selectedConsumptionYear][i].consumption = parseInt(consumption);
            this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear][i] = true;
        }
        this.cdr.markForCheck();
    }

    refreshFullness(): void {
        this.consumptionFullness = [];
        for (let i = 0; i < this.consumptions.length; i++) {
            this.consumptionFullness[i] = [];
            for (let j = 0; j < this.consumptions[i].length; j++) {
                this.consumptionFullness[i][j] = !(this.consumptions[i][j].consumption == null);
            }
        }
        this.cdr.markForCheck();
    }

    notAllDataAvailable(): boolean {
        if (!this.showErrorLaunched) {
            return false;
        }
        if (this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear] != null) {
            for (let i = 0; i < this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear].length; i++) {
                if (!this.consumptionFullness[this.yearEnd - this.selectedConsumptionYear][i]) {
                    return true;
                }
            }
        }
        return false;
    }

    updateAccountConsumption(): void {
        this.showErrorLaunched = true;
        if (this.emptyConsumptionMode && this.notAllDataAvailable()) {
            this.cdr.markForCheck();
            return;
        }
        var allConsumptions: AccountConsumption[] = [];
        for (let i = 0; i < this.consumptions.length; i++) {
            for (let j = 0; j < this.consumptions[i].length; j++) {
                allConsumptions.push(this.consumptions[i][j]);
            }
        }
        if (this.emptyConsumptionMode && !this.rewriteEstimationMode) {
            //switch previous year to new year in case of no year selected consumption
            let currentYear = this.currentDate.year;
            let previousYear = currentYear - 1;
            let currentMonth = this.currentDate.month;
            for (let i = 0; i < allConsumptions.length; i++) {
                if (allConsumptions[i].consumptionYear == previousYear && allConsumptions[i].consumptionMonth! < currentMonth) {
                    allConsumptions[i].consumptionYear = currentYear;
                }
            }
        }
        this.generalService.addUpdateAccountConsumption(this.account, allConsumptions).subscribe(
            {
                next: (res: any) => {
                    this.finished.next(true);
                    this.cdr.markForCheck();
                },
                error: (err: any) => {
                    if (err.status == 400) {
                        this.setErrorText(err.error);
                    }
                    this.cdr.markForCheck();
                }
            });
    }

    cancel(): void {
        this.clearErrors();
        this.cancelled.next(true);
        this.cdr.markForCheck();
    }
}